import { Fragment, useEffect, useMemo, useState } from "react";

import { http, qs } from "../api";
import { Badge, Empty, SkeletonRows, Toggle, useAsync } from "../ui";
import {
  Card,
  ErrorBanner,
  Loadable,
  TurnBodies,
  dash,
  fmtInt,
  fmtMs,
  fmtPct,
  num,
  rateColor,
  shortId,
} from "./format";
import type {
  EvalTurn,
  SessionDetail,
  SessionsResponse,
  ThreadsResponse,
  TurnsResponse,
} from "./types";

// ============================================================ threads tab

const THREADS_LIMIT = 200;

/**
 * The conversation is the unit the product ships. A turn pass rate cannot show a
 * thread that fell apart at turn 4 — the first three turns pass and carry the
 * average — so a thread's verdict here is BOOL_AND over its turns, worst first.
 */
export function ThreadsTab({ runId, filters }: { runId: string; filters: Record<string, string> }) {
  const key = JSON.stringify(filters);
  const [failsOnly, setFailsOnly] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const threads = useAsync<ThreadsResponse>(
    () =>
      runId
        ? http.get<ThreadsResponse>(
            `/chatbot/eval-ops/threads${qs({ run_id: runId, limit: THREADS_LIMIT, ...filters })}`
          )
        : Promise.resolve({ run_id: "", threads: [], count: 0, total: 0 }),
    [runId, key]
  );

  // The transcript window is fetched ONCE per run+filter set rather than per
  // thread: loading every turn of every thread to render a list nobody has
  // expanded is what makes a 6,000-turn run unusable, and one shared window is
  // cheaper than N per-thread calls once two threads are open.
  const [wantTurns, setWantTurns] = useState(false);
  const turns = useAsync<TurnsResponse>(
    () =>
      wantTurns && runId
        ? http.get<TurnsResponse>(`/chatbot/eval-ops/turns${qs({ run_id: runId, limit: 200, ...filters })}`)
        : Promise.resolve({ run_id: "", turns: [], count: 0 }),
    [wantTurns, runId, key]
  );

  useEffect(() => {
    setExpanded({});
    setWantTurns(false);
  }, [runId, key]);

  const turnsByThread = useMemo(() => {
    const out = new Map<string, EvalTurn[]>();
    for (const t of turns.data?.turns ?? []) {
      const list = out.get(t.conversation_id) ?? [];
      list.push(t);
      out.set(t.conversation_id, list);
    }
    for (const list of out.values()) list.sort((a, b) => a.turn_index - b.turn_index);
    return out;
  }, [turns.data]);

  const rows = threads.data?.threads ?? [];
  const total = num(threads.data?.total) || rows.length;
  const failing = rows.filter((t) => !t.passed).length;
  const visible = failsOnly ? rows.filter((t) => !t.passed) : rows;
  const capped = total > rows.length;

  const toggle = (id: string) => {
    setWantTurns(true);
    setExpanded((e) => ({ ...e, [id]: !e[id] }));
  };

  // Production sessions are LIVE traffic and never mixed into the counts above.
  const [showProduction, setShowProduction] = useState(false);
  const sessions = useAsync<SessionsResponse>(
    () =>
      showProduction
        ? http.get<SessionsResponse>(`/chatbot/eval-ops/telemetry/sessions${qs({ hours: 168, limit: 25 })}`)
        : Promise.resolve({ sessions: [] }),
    [showProduction]
  );
  const [openSession, setOpenSession] = useState("");
  const sessionDetail = useAsync<SessionDetail>(
    () =>
      openSession
        ? http.get<SessionDetail>(`/chatbot/eval-ops/telemetry/sessions/${encodeURIComponent(openSession)}`)
        : Promise.resolve({ session_id: "", turns: [] }),
    [openSession]
  );

  return (
    <div className="stack">
      <Card
        title="Threads"
        note="One row per conversation, worst first. A thread passes only if every one of its turns passed. Expand for the turn-by-turn transcript with the lane that served each turn."
        actions={<Toggle checked={failsOnly} onChange={setFailsOnly} label={`failing only (${failing})`} />}
      >
        <Loadable loading={threads.loading} error={threads.error} what="Failed to load threads" cols={8}>
          {!visible.length ? (
            <Empty
              title={failsOnly && rows.length ? "No failing threads" : "No conversations"}
              body={
                failsOnly && rows.length
                  ? `All ${rows.length} loaded threads passed.`
                  : "Nothing matched this run and filter set."
              }
            />
          ) : (
            <>
              <p className="muted" style={{ fontSize: 11 }}>
                <span className="strong">{fmtInt(total)}</span> scored ·{" "}
                <span className="strong">{fmtInt(rows.length)}</span> shown
                {capped ? ` — list capped at ${THREADS_LIMIT}` : ""}
              </p>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th style={{ width: 24 }} />
                      <th>Case</th>
                      <th>Conversation</th>
                      <th>Scam × user strata</th>
                      <th>Langs</th>
                      <th className="num">Turns</th>
                      <th className="num">Turn pass</th>
                      <th>Verdict</th>
                      <th>Lanes</th>
                      <th className="num">Total latency</th>
                      <th className="num">Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((thread) => {
                      const id = thread.conversation_id;
                      const open = Boolean(expanded[id]);
                      const list = turnsByThread.get(id) ?? [];
                      return (
                        // A row and its expanded transcript are two <tr>s, so the
                        // key belongs on the Fragment that wraps them.
                        <Fragment key={id}>
                          <tr style={{ cursor: "pointer" }} onClick={() => toggle(id)}>
                            <td className="muted">{open ? "▾" : "▸"}</td>
                            <td>{dash(thread.case_id)}</td>
                            <td className="muted" title={id}>
                              {shortId(id, 12)}
                            </td>
                            <td>
                              {dash(thread.scam_strata)} <span className="muted">×</span> {dash(thread.user_strata)}
                            </td>
                            <td>{(thread.langs ?? []).join(", ") || "—"}</td>
                            <td className="num">{thread.turns}</td>
                            <td className="num" style={{ color: rateColor(thread.turn_pass_rate, thread.turns) }}>
                              {fmtPct(thread.turn_pass_rate, 0)}{" "}
                              <span className="muted">
                                ({thread.passed_turns}/{thread.turns})
                              </span>
                            </td>
                            <td>
                              <Badge tone={thread.passed ? "good" : "critical"}>
                                {thread.passed ? "pass" : "fail"}
                              </Badge>
                            </td>
                            <td className="truncate" style={{ maxWidth: 180 }} title={(thread.lanes ?? []).join(", ")}>
                              {(thread.lanes ?? []).join(", ") || "—"}
                            </td>
                            <td className="num">{fmtMs(thread.total_latency_ms)}</td>
                            <td className="num">{fmtInt(thread.tokens)}</td>
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={11}>
                                {turns.loading ? (
                                  <SkeletonRows n={2} cols={2} />
                                ) : !list.length ? (
                                  <span className="muted" style={{ fontSize: 12 }}>
                                    No turns loaded for this thread — it falls outside the 200-turn drill-down window
                                    for this run and filter set.
                                  </span>
                                ) : (
                                  <div className="col">
                                    {list.map((turn) => (
                                      <div key={turn.turn_index} className="note">
                                        <div className="who">
                                          <span className="name">turn {turn.turn_index}</span>
                                          <span style={{ color: turn.passed ? "var(--success)" : "var(--critical)" }}>
                                            {turn.passed ? "pass" : (turn.fails ?? []).join(", ") || "fail"}
                                          </span>
                                          <span className="muted">
                                            {dash(turn.turn_owner)} · {fmtMs(turn.latency_ms)} ·{" "}
                                            {fmtInt(turn.total_tokens)} tok
                                          </span>
                                          {turn.judge_verdict && <span>judge: {turn.judge_verdict}</span>}
                                        </div>
                                        <TurnBodies turn={turn} />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <ErrorBanner error={turns.error} what="Failed to load the transcript window" />
            </>
          )}
        </Loadable>
      </Card>

      <Card
        title="Production sessions — last 7 days"
        note="Live traffic, not this run. A real conversation is the only thing that can tell you whether the eval threads above resemble anything."
        actions={
          <button className="btn sm" onClick={() => setShowProduction((v) => !v)}>
            {showProduction ? "Hide" : "Show"}
          </button>
        }
      >
        {!showProduction ? (
          <p className="muted" style={{ fontSize: 12 }}>
            Fetched on demand — it is a different population and is never folded into the counts above.
          </p>
        ) : (
          <Loadable loading={sessions.loading} error={sessions.error} what="Failed to load production sessions" cols={7}>
            {!(sessions.data?.sessions ?? []).length ? (
              <Empty title="No production sessions" body="Nothing was recorded in this window." />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Session</th>
                      <th className="num">Turns</th>
                      <th>Languages</th>
                      <th className="num">Scam turns</th>
                      <th className="num">Avg latency</th>
                      <th className="num">Tokens</th>
                      <th className="num">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sessions.data?.sessions ?? []).map((session) => (
                      <Fragment key={session.session_id}>
                        <tr
                          style={{ cursor: "pointer" }}
                          onClick={() => setOpenSession((s) => (s === session.session_id ? "" : session.session_id))}
                        >
                          <td title={session.session_id}>{shortId(session.session_id, 16)}</td>
                          <td className="num">{fmtInt(session.turns)}</td>
                          <td>{(session.languages ?? []).filter(Boolean).join(", ") || "—"}</td>
                          <td className="num">{fmtInt(session.scam_turns)}</td>
                          <td className="num">{fmtMs(session.avg_ms)}</td>
                          <td className="num">{fmtInt(session.total_tokens)}</td>
                          <td className="num" style={{ color: num(session.errors) > 0 ? "var(--critical)" : undefined }}>
                            {fmtInt(session.errors)}
                          </td>
                        </tr>
                        {openSession === session.session_id && (
                          <tr>
                            <td colSpan={7}>
                              <Loadable
                                loading={sessionDetail.loading}
                                error={sessionDetail.error}
                                what="Failed to load the session replay"
                                cols={2}
                              >
                                {!(sessionDetail.data?.turns ?? []).length ? (
                                  <Empty title="No turns in this session" />
                                ) : (
                                  <div className="col">
                                    {(sessionDetail.data?.turns ?? []).map((turn, i) => (
                                      <div key={turn.trace_id ?? i} className="note">
                                        <div className="who">
                                          <span>{dash(turn.classification)}</span>
                                          <span>{dash(turn.kg_band)}</span>
                                          <span>{dash(turn.language)}</span>
                                          <span>{fmtMs(turn.latency_ms)}</span>
                                          {turn.error_type && (
                                            <span style={{ color: "var(--critical)" }}>{turn.error_type}</span>
                                          )}
                                        </div>
                                        <div className="body">{dash(turn.input_text)}</div>
                                        <div className="body muted">{dash(turn.output_text)}</div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </Loadable>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Loadable>
        )}
      </Card>
    </div>
  );
}
